import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Modal,
  Fade,
  Typography,
  Paper,
  Button,
  Box,
  Grid,
  Divider
} from '@mui/material';
import { Input } from 'components/layout';
import { useTranslation } from 'react-i18next';
import { templatePolicies as policies } from 'helpers';
import { Controller, useForm } from 'react-hook-form';
import { TableCustom } from 'components/common';
import BlockIcon from '@mui/icons-material/Block';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';


const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 600,
  p: 3
};

const defaultFields = [
  { policy: policies.pollingVariable },
  { policy: policies.pollingDescription }
];

function formatDefaultValues(type, defaultValues) {
  if (type === 'number') {
    if (!defaultValues){
      defaultValues = {
        pollingNumberFrom: null,
        pollingNumberTo: null
      }
    } else {
      defaultValues['pollingNumberFrom'] = defaultValues.pollingRange[0];
      defaultValues['pollingNumberTo'] = defaultValues.pollingRange[1];
    }
  }

  return defaultValues;
}

export default function TemplatePollingModal(props) {
  const {
    open,
    handleClose,
    onSubmit,
    onEditSubmit,
    polling,
    currentPollings,
    defaultValues
  } = props;
  const [t] = useTranslation();
  const { isEdit, data, options } = polling;
  const actionType = isEdit ? 'edit' : 'create';
  const typeSelected = data.pollingType;
  const methods = useForm({ defaultValues: formatDefaultValues(typeSelected, defaultValues) });
  const { control, handleSubmit, getValues, setError, setValue, resetField } = methods;
  const [fields, setFields] = useState({ content: [], type: 'text'});

  useEffect(() => {
    setValue(policies.pollingType.name, typeSelected);

    if (typeSelected === options[1].value) {
      setFields({ content: [
        { policy: policies.pollingNumberFrom },
        { policy: policies.pollingNumberTo }
      ], type: typeSelected });
    } else if (typeSelected === options[2].value) {
      setFields({ content: [
        { policy: policies.pollingOptionValue },
        { policy: policies.pollingOptionDescription }
      ], type: typeSelected});
      if (!isEdit) setValue(policies.pollingOptionsValues.name, []);
      else if (!defaultValues?.pollingOptionsValues) setValue(policies.pollingOptionsValues.name, []);
    } else setFields({ content: [], type: options[0].value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeSelected]);

  const columns = [
    { label: t(policies.pollingOptionValue.label), key: policies.pollingOptionValue.name },
    { label: t(policies.pollingOptionDescription.label), key: policies.pollingOptionDescription.name },
  ];

  const actions = [
    {
      label: t('templateList.stepper.notification.table.actionDelete'),
      icon: <HighlightOffIcon color='primary'/>,
      handle: handleDeleteOption
    }
  ];

  const handleOnSubmit = (data) => {
    const pollingVariable = data[policies.pollingVariable.name];
    if (!pollingVariable) {
      setError(policies.pollingVariable.name, { type: 'required', message: 'common.rules.required' });
      return;
    }

    const variable = defaultValues?.pollingVariable || data?.pollingVariable;
    const itemIndexFound = currentPollings.findIndex((p) => p.pollingVariable === variable);

    if (isEdit) {
      if (itemIndexFound === -1) return;
      onEditSubmit(data, itemIndexFound);
      return;
    }

    if (itemIndexFound !== -1) {
      setError(policies.pollingVariable.name, { type: 'required', message: 'common.components.chipList.error' });
      return;
    }

    return onSubmit(data);
  }

  function handleDeleteOption(option) {
    const currentOptions = getValues(policies.pollingOptionsValues.name);
    const newOptions = currentOptions.filter(o =>
      o[policies.pollingOptionValue.name] !== option[policies.pollingOptionValue.name]
    );
    setValue(policies.pollingOptionsValues.name, newOptions);
  }

  function handleAddOption(data) {

    const option = {
      [policies.pollingOptionValue.name]: data[policies.pollingOptionValue.name],
      [policies.pollingOptionDescription.name]: data[policies.pollingOptionDescription.name] || ''
    }

    if (!option[policies.pollingOptionValue.name]) {
      setError(policies.pollingOptionValue.name, { });
      return;
    }

    const currentOptions = getValues(policies.pollingOptionsValues.name);

    const itemFound = currentOptions.find(o =>
      o[policies.pollingOptionValue.name] === option[policies.pollingOptionValue.name]
    );
    if (itemFound) {
      setError(policies.pollingOptionValue.name, { message: t('common.components.chipList.error') });
      return;
    }
    setValue(policies.pollingOptionsValues.name, [...currentOptions, option]);
    resetField(policies.pollingOptionValue.name);
    resetField(policies.pollingOptionDescription.name);
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      sx={{ backdropFilter: 'blur(2px)' }}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Paper sx={style}>
          <Typography id="transition-modal-title" variant="h6" component="h2">
            { t(`templateList.stepper.polling.modal.${actionType}.title`) }
          </Typography>

          <Typography id="transition-modal-description" variant='subtitle1' sx={{ mt: 1, mb: 2 }}>
            { t('templateList.stepper.polling.modal.description',
              { type: t(`common.dataSelect.pollingType.${typeSelected}`) })
            }
          </Typography>

          <Grid container spacing={2}>
            { defaultFields.map((field, index) => (
              <Grid item key={index} xs={12} sm={12} md={4} lg={6}>
                <Input
                  {...field.policy}
                  control={control}
                  sx={{ width: '100%' }}
                />
              </Grid>
            ))}
          </Grid>

          { !!fields.content.length &&
            <>
              <Divider sx={{ pb: 2 }} />

              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>{
                fields.type === 'number' ?
                  t('templateList.stepper.polling.modal.rangeSubtitle')
                  : t('templateList.stepper.polling.modal.multipleSubtitle')
              }</Typography>

              <Grid container spacing={2}>
                { fields.content.map((field, index) => (
                  <Grid
                    item
                    key={index}
                    xs={12}
                    sm={6}
                    md={typeSelected === 'number' ? 6 : 4}
                    lg={typeSelected === 'number' ? 6 : 4}
                  >
                    <Input
                      {...field.policy}
                      control={control}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                ))}
                { fields.type !== 'number' &&
                  <>
                    <Grid item xs={12} sm={6} md={4} lg={4} sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        height: '84px'
                      }}
                    >
                      <Button variant="outlined" onClick={handleSubmit(handleAddOption)}>
                        {t('templateList.stepper.polling.buttonLabel')}
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Controller
                        control={control}
                        name={policies.pollingOptionsValues.name}
                        id={policies.pollingOptionsValues.id}
                        render={({ field: { value } }) => (
                          <TableCustom
                            columns={columns}
                            content={value}
                            actions={actions}
                            config={{
                              size: 'small',
                              maxHeight: '250px',
                              notResults: {
                                icon: BlockIcon
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                }
              </Grid>
            </>
          }

          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2
          }}>
            <Button onClick={handleClose}>
              { t(`templateList.stepper.polling.modal.buttonClose`) }
            </Button>
            <Button
              onClick={handleSubmit(handleOnSubmit)}
              variant='contained'
            >
              { t(`templateList.stepper.polling.modal.${actionType}.button`) }
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}
